import * as React from 'react'

import type {
  ResultMatches,
  SEPAMatchDataType,
  SupplementsMatches,
  WorksheetData,
} from 'modules/payroll_bundle/components/common/helper/worksheet'

export type ActionTypes = 'RESET_DATA' | 'UPDATE_DATA'

export interface ResetDataAction {
  type: 'RESET_DATA'
}

export interface UpdateDataAction {
  type: 'UPDATE_DATA'
  payload: Omit<Partial<State>, 'SEPADispatch'>
}

export type Actions = ResetDataAction | UpdateDataAction

const sepaContext = React.createContext<State | null>(null)

const SepaProvider = sepaContext.Provider

export interface ImportedEmployee {
  IBAN: string
  amount: string
  full_name: string
  last_name: string
  name: string
  userId: number
  identifier: string
}

interface CustomValue {
  value: string
  label: string
}

interface State {
  sepaStep: number
  matchedData: SEPAMatchDataType[]
  activeButton: string
  customValues: CustomValue[]
  worksheetData: WorksheetData
  worksheetMatches: ResultMatches | SupplementsMatches
  employeesSelected: number[]
  importedEmployees: ImportedEmployee[]
  SEPADispatch: React.Dispatch<Actions>
}

const initialState = {
  sepaStep: 1,
  matchedData: [],
  activeButton: '',
  customValues: [],
  worksheetData: [],
  worksheetMatches: {},
  employeesSelected: [],
  importedEmployees: [],
  SEPADispatch: () => null,
}

function reducer(state: State, action: Actions): State {
  switch (action.type) {
    case 'UPDATE_DATA':
      return { ...state, ...action.payload }

    case 'RESET_DATA':
      return initialState

    default:
      throw new Error()
  }
}

export const SepaContextProvider = ({ children }: React.PropsWithChildren) => {
  const [
    {
      sepaStep,
      matchedData,
      activeButton,
      customValues,
      worksheetData,
      worksheetMatches,
      employeesSelected,
      importedEmployees,
    },
    dispatch,
  ] = React.useReducer<React.Reducer<State, Actions>>(reducer, initialState)

  const values = {
    sepaStep,
    employeesSelected,
    importedEmployees,
    activeButton,
    worksheetMatches,
    matchedData,
    customValues,
    worksheetData,
    SEPADispatch: dispatch,
  }

  return <SepaProvider value={values}>{children}</SepaProvider>
}

export const useSepa = () => {
  const results = React.useContext(sepaContext)

  if (!results) {
    throw new Error('useSepa hook must be used within a SepaProvider provider')
  }

  return results
}
